import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import QtyControl from './QtyControl';
import type { MenuCardProps } from '../types';

const MAX_VISIBLE_VARIANTS = 3;

const MenuCard: React.FC<MenuCardProps> = ({
    item,
    category: _category,
    name,
    selectedVariant,
    inCartQty,
    onAdd,
    onIncrement,
    onDecrement,
    onVariantChange,
}) => {
    const [showAllVariants, setShowAllVariants] = useState(false);
    const [imgError, setImgError] = useState(false);
    const hasMultipleVariants = item.variants.length > 1;
    const hiddenCount = item.variants.length - MAX_VISIBLE_VARIANTS;
    const visibleVariants = showAllVariants
        ? item.variants
        : item.variants.slice(0, MAX_VISIBLE_VARIANTS);

    const hasImage = !!item.image && !imgError;
    const foodType = item.type || 'veg';
    const typeLabel = foodType === 'non-veg' ? 'Non-Veg' : foodType === 'egg' ? 'Egg' : 'Veg';

    return (
        <div
            className={`posv2-menu-card food-indicator food-indicator--${foodType}`}
            onClick={onAdd}
        >
            <span className="posv2-menu-card__type-badge" aria-label={typeLabel}>
                {typeLabel}
            </span>
            <div className="posv2-menu-card__img">
                {hasImage ? (
                    <img
                        src={item.image}
                        alt={name}
                        loading="lazy"
                        onError={() => setImgError(true)}
                    />
                ) : (
                    <div className="posv2-menu-card__placeholder">
                        <RestaurantIcon className="posv2-menu-card__placeholder-icon" />
                    </div>
                )}
            </div>

            <div className="posv2-menu-card__info">
                <p className="posv2-menu-card__name">{name.replaceAll('-', ' ')}</p>

                {hasMultipleVariants ? (
                    <div
                        className="posv2-menu-card__variants"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {visibleVariants.map((v, i) => (
                            <button
                                key={i}
                                className={`posv2-variant-btn ${selectedVariant.value === v.cost ? 'active' : ''}`}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onVariantChange(v);
                                }}
                            >
                                {v.size} - ₹{v.cost}
                            </button>
                        ))}
                        {hiddenCount > 0 && !showAllVariants && (
                            <button
                                className="posv2-variant-btn posv2-variant-btn--more"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllVariants(true);
                                }}
                            >
                                +{hiddenCount} more
                            </button>
                        )}
                        {showAllVariants && hiddenCount > 0 && (
                            <button
                                className="posv2-variant-btn posv2-variant-btn--more"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setShowAllVariants(false);
                                }}
                            >
                                Show less
                            </button>
                        )}
                    </div>
                ) : (
                    <p className="posv2-menu-card__price">
                        ₹{item.variants[0].cost} / {item.variants[0].size}
                    </p>
                )}
            </div>

            {inCartQty > 0 ? (
                <div className="posv2-menu-card__badge">
                    <QtyControl
                        quantity={inCartQty}
                        onIncrement={onIncrement}
                        onDecrement={onDecrement}
                        size="md"
                    />
                </div>
            ) : (
                <div className="posv2-menu-card__add-btn">
                    <AddIcon sx={{ fontSize: 24 }} />
                </div>
            )}
        </div>
    );
};

export default React.memo(MenuCard);
